import { useState } from 'react';
import { isAxiosError } from 'axios';
import { CreateLeadPayload, createLead, updateLead } from '../../../api/leads';
import { LeadDetail } from '../../../types/lead';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to save') : 'Failed to save';

/** Persists the Lead Registration Wizard's progress to the backend as the counselor
 * advances through steps: creates the lead as a Draft on Step 1, then PATCHes on every
 * subsequent step so the draft can be resumed later (from a different session/device). */
export const useLeadDraftAutosave = () => {
  const [leadId, setLeadId] = useState<number | null>(null);
  const [leadNumber, setLeadNumber] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const saveStep = async (payload: CreateLeadPayload, lastCompletedStep: number): Promise<LeadDetail | null> => {
    setSaving(true);
    setError(null);
    try {
      let lead: LeadDetail;
      if (!leadId) {
        lead = await createLead({ ...payload, saveAsDraft: true, lastCompletedStep });
      } else {
        lead = await updateLead(leadId, { ...payload, lastCompletedStep });
      }
      setLeadId(lead.id);
      setLeadNumber(lead.leadNumber);
      setSavedAt(new Date());
      return lead;
    } catch (err) {
      setError(extractErrorMessage(err));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const hydrate = (lead: LeadDetail) => {
    setLeadId(lead.id);
    setLeadNumber(lead.leadNumber);
    setSavedAt(null);
    setError(null);
  };

  const reset = () => {
    setLeadId(null);
    setLeadNumber(null);
    setSavedAt(null);
    setError(null);
  };

  return { leadId, leadNumber, saving, error, savedAt, saveStep, hydrate, reset };
};
